// Browser-side PII sanitization that produces values matching Meta's
// Conversions API + manual Advanced Matching specs. We store these
// pre-normalized in the cookie/localStorage so server-side hashing and
// browser-side hashing produce identical SHA-256 outputs.
//
// Spec refs:
//   https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
//   https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/

import { META_NORMALIZERS } from '../src/lib/hash';

const HONORIFIC_RE = /^(?:mr|mrs|ms|miss|mx|dr|prof|sir|madam|rev|fr)\.?\s+/i;
const SUFFIX_RE    = /\s+(?:jr|sr|ii|iii|iv|v|phd|md|esq|cpa|dds|jd)\.?$/i;

export interface SanitizedPii {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export function splitName(raw: string): { first?: string; last?: string } {
  if (!raw) return {};
  let cleaned = raw.trim().replace(/\s+/g, ' ');
  cleaned = cleaned.replace(HONORIFIC_RE, '').replace(SUFFIX_RE, '').trim();
  if (!cleaned) return {};

  const parts = cleaned.split(' ').filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { first: parts[0]! };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

export interface RawPii {
  name?: string;          // single-field name (StealthSeminar, ClickFunnels)
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export function sanitizePii(raw: RawPii): SanitizedPii {
  const out: SanitizedPii = {};

  if (raw.email) {
    const v = META_NORMALIZERS.em(raw.email);
    if (v) out.email = v;
  }
  if (raw.phone) {
    const v = META_NORMALIZERS.ph(raw.phone);
    if (v) out.phone = v;
  }
  if (raw.city) {
    const v = META_NORMALIZERS.ct(raw.city);
    if (v) out.city = v;
  }
  if (raw.state) {
    const v = META_NORMALIZERS.st(raw.state);
    if (v) out.state = v;
  }
  if (raw.zip) {
    const v = META_NORMALIZERS.zp(raw.zip);
    if (v) out.zip = v;
  }
  if (raw.country) {
    const v = META_NORMALIZERS.country(raw.country);
    if (v) out.country = v;
  }

  let first = raw.first_name;
  let last = raw.last_name;
  if ((!first || !last) && raw.name) {
    const parts = splitName(raw.name);
    if (!first && parts.first) first = parts.first;
    if (!last && parts.last) last = parts.last;
  }
  if (first) {
    const v = META_NORMALIZERS.fn(first);
    if (v) out.first_name = v;
  }
  if (last) {
    const v = META_NORMALIZERS.ln(last);
    if (v) out.last_name = v;
  }

  return out;
}
